import {Nullable, Options} from '@fl/cmsch-fe-library';
import {createSelector} from 'reselect';
import {Opt} from 'ts-opt';

import {AuthDiscriminatorDetail} from 'api/gen/AuthDiscriminatorDetail';
import {ColumnHeader} from 'api/gen/ColumnHeader';
import {Farm} from 'api/gen/Farm';
import {PagedModelAssociationRole} from 'api/gen/PagedModelAssociationRole';
import {PagedModelBasicRole} from 'api/gen/PagedModelBasicRole';
import {PagedModelBreederRole} from 'api/gen/PagedModelBreederRole';
import {PagedModelOrganizationRole} from 'api/gen/PagedModelOrganizationRole';
import {PagedModelOtherSubjectRole} from 'api/gen/PagedModelOtherSubjectRole';
import {PagedModelPermissionDetail} from 'api/gen/PagedModelPermissionDetail';
import {PagedModelRolePermission} from 'api/gen/PagedModelRolePermission';
import {PagedModelRoleView} from 'api/gen/PagedModelRoleView';
import {PagedModelUserProfileContact} from 'api/gen/PagedModelUserProfileContact';
import {PagedModelUserSubjectView} from 'api/gen/PagedModelUserSubjectView';
import {PagedModelUserView} from 'api/gen/PagedModelUserView';
import {PermissionDetail} from 'api/gen/PermissionDetail';
import {RoleDetail} from 'api/gen/RoleDetail';
import {RoleView} from 'api/gen/RoleView';
import {UserDetail} from 'api/gen/UserDetail';
import {UserProfileAssociationRole} from 'api/gen/UserProfileAssociationRole';
import {UserProfileBreederRoleFarms} from 'api/gen/UserProfileBreederRoleFarms';
import {UserProfileOrganizationRoleFarmsCenters} from 'api/gen/UserProfileOrganizationRoleFarmsCenters';
import {UserProfileOtherSubjectRole} from 'api/gen/UserProfileOtherSubjectRole';
import {State} from 'app/setup';
import {genRoleOption} from 'utils/gen-role-option';

import {AdminLoadingId} from '../types/admin-loading-id';
import {ManagementAssociationsPage} from '../types/management-associations';
import {ManagementBreedersPage} from '../types/management-breeders';
import {ManagementOrganizationsPage} from '../types/management-organizations';
import {ManagementOtherSubjectsPage} from '../types/management-other-subjects';
import {getCodeNameString} from '../utils/get-code-name-string';

/** Selectors for the admin module state slice. */
export const simpleAdminSelector = {
    rolePermissionsPage: ({admin}: State): Opt<PagedModelRolePermission> => admin.rolePermissionsPage,
    roleUnassignedPermissionsPage: ({admin}: State): Opt<PagedModelRolePermission> =>
        admin.roleUnassignedPermissionsPage,
    rolesPage: ({admin}: State): Opt<PagedModelRoleView> => admin.rolesPage,
    permissionDetailsPage: ({admin}: State): Opt<PagedModelPermissionDetail> => admin.permissionDetailsPage,
    editedPermissionId: ({admin}: State): number | null => admin.permissionIdInEdit,
    editedRoleId: ({admin}: State): number | null => admin.roleIdInEdit,
    permissionCreateVisible: ({admin}: State): boolean => admin.permissionCreateVisible,
    roleCreateModalVisible: ({admin}: State): boolean => admin.roleCreateModalVisible,
    permissionDetailsTableData: ({admin}: State): Array<PermissionDetail> =>
        admin.permissionDetailsPage.prop('content').orElse([]),
    roleDetailsTableData: ({admin}: State): Array<RoleView> =>
        admin.rolesPage.prop('content').orElse([]),
    roleDetail: ({admin}: State): Opt<RoleDetail> => admin.roleDetail,
    subordinateAssignedRolePage: ({admin}: State): Opt<PagedModelBasicRole> => admin.subordinateAssignedRolePage,
    subordinateUnassignedRolePage: ({admin}: State): Opt<PagedModelBasicRole> => admin.subordinateUnassignedRolePage,
    usersPage: ({admin}: State): Opt<PagedModelUserView> => admin.usersPage,
    userDetail: ({admin}: State): Opt<UserDetail> => admin.userDetail,
    memberOfSubjectsPage: ({admin}: State): Opt<PagedModelUserSubjectView> => admin.memberOfSubjects,
    userContacts: ({admin}: State): Opt<PagedModelUserProfileContact> => admin.userContacts,
    userAssociations: ({admin}: State): Opt<PagedModelAssociationRole> => admin.userAssociations,
    userBreeders: ({admin}: State): Opt<PagedModelBreederRole> => admin.userBreeders,
    userOtherSubjectRoles: ({admin}: State): Opt<PagedModelOtherSubjectRole> => admin.userOtherSubjectRoles,
    organizations: ({admin}: State): Opt<PagedModelOrganizationRole> => admin.organizations,
    loading: ({admin}: State): Array<AdminLoadingId> => admin.loading,
    managementBreedersPage: ({admin}: State): Opt<ManagementBreedersPage> => admin.managementBreedersPage,
    managementBreedersOptions: ({admin}: State): Options<number> => admin.managementBreedersOptions,
    managementBreedersExtraColumns: ({admin}: State): Array<ColumnHeader> => admin.managementBreedersExtraColumns,
    managementBreedersFarms: ({admin}: State): Array<Farm> => admin.managementBreedersFarms,
    subjectManagementModalVisible: ({admin}: State): Nullable<string> => admin.managementSubjectModalVisible,
    managementBreederDetail: ({admin}: State): Opt<UserProfileBreederRoleFarms> => admin.managementBreederDetail,
    managementAssociationsOptions: ({admin}: State): Options<number> => admin.managementAssociationsOptions,
    managementAssociationsPage: ({admin}: State): Opt<ManagementAssociationsPage> => admin.managementAssociationsPage,
    managementAssociationsExtraColumns: ({admin}: State): Array<ColumnHeader> =>
        admin.managementAssociationsExtraColumns,
    managementAssociationDetail: ({admin}: State): Opt<UserProfileAssociationRole> => admin.managementAssociationDetail,
    discriminators: ({admin}: State): Array<AuthDiscriminatorDetail> => admin.adminDiscriminators,

    managementOtherSubjectsOptions: ({admin}: State): Options<number> => admin.managementOtherSubjectsOptions,
    managementOtherSubjectsPage: ({admin}: State): Opt<ManagementOtherSubjectsPage> =>
        admin.managementOtherSubjectsPage,
    managementOtherSubjectsExtraColumns: ({admin}: State): Array<ColumnHeader> =>
        admin.managementOtherSubjectsExtraColumns,
    managementOtherSubjectDetail: ({admin}: State): Opt<UserProfileOtherSubjectRole> =>
        admin.managementOtherSubjectDetail,

    managementOrganizationsOptions: ({admin}: State): Options<number> => admin.managementOrganizationsOptions,
    managementOrganizationsPage: ({admin}: State): Opt<ManagementOrganizationsPage> =>
        admin.managementOrganizationsPage,
    managementOrganizationsExtraColumns: ({admin}: State): Array<ColumnHeader> =>
        admin.managementOrganizationsExtraColumns,
    managementOrganizationDetail: ({admin}: State): Opt<UserProfileOrganizationRoleFarmsCenters> =>
        admin.managementOrganizationDetail,
    managementOrganizationFarms: ({admin}: State): Array<Farm> => admin.managementOrganizationFarms,
    managementOrganizationCenters: ({admin}: State): Array<Farm> => admin.managementOrganizationCenters,
};

export const managementBreedersRoleOptionsSelector = createSelector(
    (state: State) => state.admin.managementBreedersRoles,
    genRoleOption,
);

export const managementBreedersUserOptionsSelector = createSelector(
    (state: State) => state.admin.managementBreedersUsers,
    options => options.map(o => ({value: o.userProfileId, label: o.username})),
);

export const managementBreedersFarmOptionsSelector = createSelector(
    (state: State) => state.admin.managementBreedersFarms,
    options => options.map(o => ({value: o.id, label: `${o.code}${o.name ? ` - ${o.name}` : ``}`})),
);
export const adminDiscriminatorOptionsSelector = createSelector(
    (state: State) => state.admin.adminDiscriminators,
    (discriminators: Array<AuthDiscriminatorDetail>): Options<number> =>
        discriminators.map(o => ({value: o.id, label: getCodeNameString(o.code, o.name)})),
);

export const managementAssociationsUserOptionsSelector = createSelector(
    (state: State) => state.admin.managementAssociationsUsers,
    options => options.map(o => ({value: o.userProfileId, label: o.username})),
);

export const managementAssociationsRoleOptionsSelector = createSelector(
    (state: State) => state.admin.managementAssociationsRoles,
    genRoleOption,
);

export const managementOtherSubjectsUserOptionsSelector = createSelector(
    (state: State) => state.admin.managementOtherSubjectsUsers,
    options => options.map(o => ({value: o.userProfileId, label: o.username})),
);

export const managementOtherSubjectsRoleOptionsSelector = createSelector(
    (state: State) => state.admin.managementOtherSubjectsRoles,
    genRoleOption,
);

export const managementOrganizationsUserOptionsSelector = createSelector(
    (state: State) => state.admin.managementOrganizationsUsers,
    options => options.map(o => ({value: o.userProfileId, label: o.username})),
);

export const managementOrganizationsRoleOptionsSelector = createSelector(
    (state: State) => state.admin.managementOrganizationsRoles,
    genRoleOption,
);

export const managementOrganizationFarmOptionsSelector = createSelector(
    simpleAdminSelector.managementOrganizationFarms,
    options => options.map(o => ({value: o.id, label: `${o.code}${o.name ? ` - ${o.name}` : ``}`})),
);

export const managementOrganizationCenterOptionsSelector = createSelector(
    simpleAdminSelector.managementOrganizationCenters,
    options => options.map(o => ({value: o.id, label: `${o.code}${o.name ? ` - ${o.name}` : ``}`})),
);

export const managementOrganizationFarmOptionsTooltipSelector = createSelector(
    simpleAdminSelector.managementOrganizationFarms,
    options => options.map(o => ({value: o.id, label: o.code})),
);

export const managementOrganizationCenterOptionsTooltipSelector = createSelector(
    simpleAdminSelector.managementOrganizationCenters,
    options => options.map(o => ({value: o.id, label: o.code})),
);
